import { StepComponent } from "../../../../shared/components/ModalFlow/types";
import { FlowUploadDocumentProps } from "./types";
import {
  WSActions,
  WSAlert,
  WSCard,
  WSInfoBox,
  WSList,
  WSProgressBar
} from "@wingspanhq/fe-component-library";
import { useQueryInsuranceImport } from "../../../../query/insuranceImport/queries/useQueryInsuranceImport";
import { getPolicies } from "./utils";
import { useCallback, useEffect, useState } from "react";
import { useMutationSubmitInsuranceImport } from "../../../../query/insuranceImport/mutations/useMutationSubmitInsuranceImport";

const Submitting: React.FC = () => {
  const [progress, setProgress] = useState(10);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 1000);
  }, []);

  return (
    <WSList gap="L">
      <WSCard
        header={{
          label: "Insurance policies",
          value: {
            pill: {
              text: "Importing",
              theme: "blue"
            }
          }
        }}
      >
        <WSProgressBar
          my="L"
          percent={progress}
          theme="neutral"
          text={
            <WSInfoBox mt="S" theme="info" icon="time-five" transparent>
              Importing in progress, please wait...
            </WSInfoBox>
          }
        />
      </WSCard>
    </WSList>
  );
};

export const Step3: StepComponent<FlowUploadDocumentProps> = ({
  state,
  setState,
  onNext,
  onClose,
  onBack,
  children,
  goToSideStep
}) => {
  const { insuranceImportId } = state;

  const [submitInsurance, submitInsuranceMeta] =
    useMutationSubmitInsuranceImport();

  const queryInsuranceImport = useQueryInsuranceImport(insuranceImportId!, {
    enabled: !!insuranceImportId
  });

  const extractionJob =
    queryInsuranceImport.data?.jobs.find(job => job.type === "Extraction") ||
    undefined;

  const allPolicies = extractionJob ? getPolicies(extractionJob) : [];
  const validPolicies = extractionJob ? getPolicies(extractionJob, true) : [];

  const all = allPolicies.length;
  const success = validPolicies.length;

  const [retry, setRetry] = useState(0);
  const [progress, setProgress] = useState(10);

  const submit = useCallback(async () => {
    submitInsuranceMeta.reset();
    await submitInsurance(
      {
        insuranceImportId: insuranceImportId!
      },
      {
        onSuccess() {
          goToSideStep("success");
        }
      }
    );
  }, [insuranceImportId]);

  useEffect(() => {
    setProgress(10);
    submit();

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [insuranceImportId, submit, retry]);

  return (
    <WSList gap="2XL">
      {submitInsuranceMeta.isError ? (
        <>
          <WSList gap="L">
            <WSAlert theme="error" title="Import failed" icon="alert-circle">
              Something went wrong with the import. Please try the import again
              or upload a new file and start over. If the issue persists, please
              contact support.
            </WSAlert>
            <WSCard
              header={{
                label: "Insurance policies",
                value: {
                  pill: { text: "Import failed", theme: "error" }
                }
              }}
            >
              <WSInfoBox theme="warning" icon="alert-circle" transparent>
                {submitInsuranceMeta.error?.response?.data?.error}
              </WSInfoBox>
            </WSCard>
          </WSList>
          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Cancel",
                name: "cancel",
                onClick: onClose,
                kind: "Tertiary",
                type: "button"
              },
              {
                name: "upload-new-file",
                label: "Upload new file",
                onClick: async () => {
                  setState({
                    fileId: undefined,
                    insuranceImportId: undefined
                  });
                  onBack();
                },
                kind: "Secondary",
                type: "button"
              },
              {
                label: "Try import again",
                kind: "Primary",
                onAsyncClick: async () => {
                  setRetry(retry => retry + 1);
                }
              }
            ]}
          />
        </>
      ) : (
        <>
          <WSCard
            header={{
              label: "Insurance policies",
              value: {
                pill: {
                  text: "Importing",
                  theme: "blue"
                }
              }
            }}
          >
            <WSProgressBar
              my="L"
              percent={progress}
              theme="neutral"
              text={
                <WSInfoBox mt="S" theme="info" icon="time-five" transparent>
                  {success} of {all} policies imported. If you close this page,
                  the progress will continue until it is completed.
                </WSInfoBox>
              }
            />
          </WSCard>
          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Continue",
                disabled: true
              }
            ]}
          />
        </>
      )}

      {/* <Policies policies={validPolicies} /> */}
    </WSList>
  );
};
